import type { ZudokuConfig } from "zudoku";
import legacyOperationRedirects from "./scripts/redirect-map.json";
import sidebarMap from "./scripts/sidebar-map.json";
import { OperationPage } from "./src/pages/OperationPage";

import smsSpec from "./apis/sms.oas.json";
import campaignSpec from "./apis/campaign.oas.json";
import viberSpec from "./apis/viber.oas.json";
import verificationSpec from "./apis/verification.oas.json";
import voiceSpec from "./apis/voice.oas.json";
import balanceSpec from "./apis/balance.oas.json";
import contactSpec from "./apis/contact.oas.json";
import audienceSpec from "./apis/audience.oas.json";
import chatappsSpec from "./apis/chatapps.oas.json";

const specsBySpecName: Record<string, any> = {
  sms: smsSpec,
  campaign: campaignSpec,
  viber: viberSpec,
  verification: verificationSpec,
  voice: voiceSpec,
  balance: balanceSpec,
  contact: contactSpec,
  audience: audienceSpec,
  chatapps: chatappsSpec,
};

// Zudoku's per-API sidebar (from `apis[]`) only shows the operations of
// whichever mount the current URL is under — by design, its own docs call
// this "stacking" for large multi-API sites. That means navigating from an
// SMS operation to a WhatsApp one visually collapses SMS, and getting back
// means re-expanding it — not how ReadMe's sidebar behaved (open a group
// once, it stays open no matter where you navigate next).
//
// Fix: don't rely on the auto-generated per-mount sidebar for navigation at
// all. Build the full 10-group / 36-operation tree as plain static links
// straight from sidebar-map.json (the same data prepare-oas.mjs uses), so
// it's part of the one global `navigation` tree and identical on every
// page. The `apis[]` mounts still render the actual operation pages
// (params, schemas, code samples, try-it) — they just no longer drive what
// the sidebar looks like.
// Matches the badge coloring used in src/components/OperationForm.tsx's
// method pill, using the closest colors Zudoku's nav badge supports.
const METHOD_BADGE_COLOR = {
  get: "blue",
  post: "green",
  put: "yellow",
  patch: "yellow",
  delete: "red",
} as const;

const apiGroupCategories = sidebarMap.groups.map((group) => {
  const spec = specsBySpecName[group.spec];
  const operationLinks = group.operations.map((op) => ({
    type: "custom-page" as const,
    path: `${group.mountPath}/${op.slug.replace(/_/g, "-")}`,
    element: <OperationPage spec={spec} operationId={op.slug} />,
    label: op.title,
    badge: {
      label: op.method.toUpperCase(),
      color: METHOD_BADGE_COLOR[op.method as keyof typeof METHOD_BADGE_COLOR],
    },
  }));
  const siblingDocLinks = group.siblingDocs.map((doc) => ({
    type: "doc" as const,
    file: doc.file,
    label: doc.label,
  }));

  return {
    type: "category" as const,
    label: group.navTitle,
    collapsible: false,
    items: [...operationLinks, ...siblingDocLinks],
  };
});

const generalInfoCategory = {
  type: "category" as const,
  label: sidebarMap.generalInfo.navTitle,
  collapsible: false,
  items: sidebarMap.generalInfo.docs.map((doc) => ({
    type: "doc" as const,
    file: doc.file,
    label: doc.label,
  })),
};

// ReadMe's operation slugs used a bare underscore after the HTTP method
// (post_sms, get_sms-scheduled). Zudoku kebab-cases tag-derived operation
// routes (post-sms, get-sms-scheduled), so exact-slug parity isn't
// achievable — see scripts/prepare-oas.mjs and the plan's URL-parity
// section. This 301-redirects every old ReadMe operation URL to its new
// Zudoku route so existing inbound links (website, dashboard) don't break.
const config: ZudokuConfig = {
  // `site.title` also feeds the document <title> tag, so it has to stay
  // set even though we don't want its default header rendering — the
  // "hide the default header logo" rule in customCss below takes care of
  // that instead of leaving this unset (which blanks the page <title>).
  site: {
    title: "Movider API Reference",
  },
  // Stacked logo-over-text header mark instead of the default inline logo.
  slots: {
    "head-navigation-start": () => (
      <a href="/" className="flex flex-col items-center gap-0.5">
        <img src="/logo-movider.svg" alt="Movider" className="h-6 dark:hidden" />
        <img
          src="/logo-movider-white.svg"
          alt="Movider"
          className="h-6 hidden dark:block"
        />
        <span className="text-[11px] leading-none text-muted-foreground">
          API Reference
        </span>
      </a>
    ),
  },
  metadata: {
    title: "Movider API Reference",
    description:
      "Communication APIs for SMS, Verify, Voice, Viber, WhatsApp, Campaigns and Audiences.",
    favicon: "/favicon.ico",
  },
  // Self-hosted, no external search service (Algolia etc.) — Pagefind builds
  // a static search index from `dist/` at build time.
  search: { type: "pagefind" },
  theme: {
    // Movider brand palette, sourced from the real movider.co stylesheet
    // (mvd-web-nextjs/public/css/main.css:190-269) — not from
    // mvd-cms-web-nextjs's tokens.ts, whose primaryActive/accent/border
    // values were hand-eyeballed and don't appear in the live site's CSS.
    light: {
      background: "#ffffff",
      foreground: "#1a3454",
      card: "#ffffff",
      cardForeground: "#1a3454",
      popover: "#ffffff",
      popoverForeground: "#1a3454",
      primary: "#2fa1fa",
      primaryForeground: "#ffffff",
      secondary: "#f5f6f8",
      secondaryForeground: "#1a3454",
      muted: "#f5f7fe",
      mutedForeground: "#6b7589",
      accent: "#e5ecfd",
      accentForeground: "#1a3454",
      destructive: "#ff0071",
      destructiveForeground: "#ffffff",
      border: "#eeeeee",
      input: "#eeeeee",
      ring: "#2fa1fa",
      radius: "0.625rem",
    },
    dark: {
      background: "#0a1628",
      foreground: "#e7e9f5",
      card: "#0e1d33",
      cardForeground: "#e7e9f5",
      popover: "#0e1d33",
      popoverForeground: "#e7e9f5",
      primary: "#33b2f6",
      primaryForeground: "#0a1628",
      secondary: "#132540",
      secondaryForeground: "#e7e9f5",
      muted: "#132540",
      mutedForeground: "#8c94a4",
      accent: "#1a3454",
      accentForeground: "#e7e9f5",
      destructive: "#ff0071",
      destructiveForeground: "#ffffff",
      border: "#1a3454",
      input: "#1a3454",
      ring: "#33b2f6",
      radius: "0.625rem",
    },
    fonts: {
      sans: "Poppins",
    },
    customCss: `
      @font-face {
        font-family: 'Poppins';
        font-style: normal;
        font-weight: 400;
        font-display: swap;
        src: url('/fonts/poppins-400.woff2') format('woff2');
      }
      @font-face {
        font-family: 'Poppins';
        font-style: normal;
        font-weight: 500;
        font-display: swap;
        src: url('/fonts/poppins-500.woff2') format('woff2');
      }
      @font-face {
        font-family: 'Poppins';
        font-style: normal;
        font-weight: 600;
        font-display: swap;
        src: url('/fonts/poppins-600.woff2') format('woff2');
      }
      @font-face {
        font-family: 'Poppins';
        font-style: normal;
        font-weight: 700;
        font-display: swap;
        src: url('/fonts/poppins-700.woff2') format('woff2');
      }
      @font-face {
        font-family: 'Poppins';
        font-style: normal;
        font-weight: 800;
        font-display: swap;
        src: url('/fonts/poppins-800.woff2') format('woff2');
      }
      @font-face {
        font-family: 'Poppins';
        font-style: normal;
        font-weight: 900;
        font-display: swap;
        src: url('/fonts/poppins-900.woff2') format('woff2');
      }
      /* Code-sample language tabs (cURL/Go/Node/Python/PHP): scroll on
         narrow widths (the 380px sidecar) instead of wrapping/overflowing.
         min-width: 0 is required — the tablist is a flex-1 flex child, and
         flex items default to min-width: auto, so without this it refuses
         to shrink below its content's width, growing the whole row instead
         of scrolling internally (the copy button then gets clipped by the
         wrapper's own overflow-hidden). Classic flexbox-scroll gotcha. */
      .code-block-wrapper [role="tablist"] {
        overflow-x: auto;
        flex-wrap: nowrap;
        min-width: 0;
        scrollbar-width: thin;
      }
      .code-block-wrapper [role="tablist"] [role="tab"] {
        flex-shrink: 0;
      }
      /* Hide the default header logo/title (site.title stays set for the
         document <title> tag) — the head-navigation-start slot above
         replaces it visually with the stacked logo + "API Reference". */
      header .justify-self-start > a:first-child {
        display: none;
      }
      /* Sidebar nav: wider column + smaller text than Zudoku's default
         (text-sm/18rem reads oversized with Poppins' tall x-height at this
         width). data-pagefind-ignore="all" + a nested <nav> uniquely
         identifies the left sidebar (the only other data-pagefind-ignore
         regions — pagination, TOC, header — don't contain a <nav>). */
      :root {
        --side-nav-width: 22rem;
      }
      [data-pagefind-ignore="all"] nav {
        font-size: 0.8125rem;
      }
      /* Markdown doc pages (GENERAL API INFORMATION + the DR/SMPP/Voice
         reference docs) title themselves via Zudoku's Heading component at
         level 1 — text-4xl font-extrabold (36px/800) — which reads much
         heavier than the operation pages' h1 (OperationPage.tsx: text-3xl
         font-semibold, 30px/600). Match that instead of leaving the doc
         h1 oversized; body copy keeps Typography's own default prose-base
         size unchanged. Scoped to the page-title h1 specifically — inside
         the data-pagefind-filter="section:markdown" wrapper MdxPage.tsx
         puts around standalone doc pages, so it doesn't touch the inline
         field-description Markdown in OperationForm.tsx (no such wrapper
         there), and .typography h1 only matches the one title heading
         since these docs don't otherwise use a level-1 heading in body
         content (their sections start at ###/h3). */
      [data-pagefind-filter="section:markdown"] .typography h1 {
        font-size: 1.875rem;
        font-weight: 600;
      }
      /* "Example Request" sidecar (OperationForm.tsx): CodeTabs is a Zudoku
         component and doesn't take a className, so its code content
         (.code-block, text-sm/14px) can't be sized via props like the
         Response box next to it already does (SyntaxHighlight
         className="text-xs"). Scoped to .example-request-code (added on
         that SidecarBox.Body only) so it doesn't touch markdown code
         fences elsewhere on doc pages — matches the Response box's size
         for visual consistency (feedback 2026-09-17). Two class selectors
         beat Tailwind's single-class text-sm utility on specificity, so
         no !important needed. */
      .example-request-code .code-block {
        font-size: 0.75rem;
      }
    `,
  },
  // Zudoku only registers 17 grammars by default (shellscript, javascript,
  // python, java, go, json/yaml/html/css/markdown…), and naming this option
  // *replaces* that list rather than extending it — so the defaults are
  // repeated here verbatim, then the languages the Example Request panel can
  // actually emit are appended. Without them the code samples render as
  // plain unhighlighted text, which is how PHP — the whole point of the
  // library selector — was shipping (feedback 2026-09-18).
  syntaxHighlighting: {
    languages: [
      // Zudoku's defaults, kept as-is.
      "shellscript", "javascript", "jsx", "typescript", "tsx", "json",
      "jsonc", "yaml", "python", "java", "go", "graphql", "html", "css",
      "xml", "markdown", "mdx",
      // Every remaining httpsnippet target — see src/components/CodeSamples.tsx.
      "php", "ruby", "csharp", "kotlin", "swift", "rust", "powershell", "c",
      "objective-c", "clojure", "crystal", "r", "ocaml", "http",
    ],
  },
  navigation: [
    {
      type: "category",
      label: "API Reference",
      items: [...apiGroupCategories, generalInfoCategory],
    },
  ],
  redirects: [
    // Land visitors straight on the first real operation (Send SMS) rather
    // than an index/group-overview page — matches ReadMe's old behavior for
    // these entry points (feedback 2026-09-17).
    { from: "/", to: "/reference/sms/post-sms" },
    { from: "/reference", to: "/reference/sms/post-sms" },
    { from: "/reference/sms", to: "/reference/sms/post-sms" },
    ...legacyOperationRedirects,
  ],
  // Mounts the *stub* (empty-paths) copy of each spec — see
  // scripts/generate-stubs.mjs for why: Zudoku's auto-generated operation
  // page always wins over a custom-page registered at the same URL, so the
  // real spec (with paths) would fight OperationPage for all 36 routes.
  // Only the group overview (`/reference/<mount>`) and `~schemas` pages are
  // still served from these mounts; they don't need `paths` to render.
  apis: sidebarMap.groups.map((group) => ({
    type: "file" as const,
    input: `./apis/_stubs/${group.spec}.stub.oas.json`,
    options: { expandAllTags: true },
    path: group.mountPath,
  })),
};

export default config;
